<template>
  <v-row>
    <!-- Header -->
    <v-col cols="12" class="d-flex justify-end pb-2">
      <v-btn
        small
        rounded
        elevation="2"
        color="#E65100"
        class="white--text font-weight-bold"
        :to="{ name: 'manager.investments.validate' }"
      >
        <v-icon small left>fas fa-tasks</v-icon>
        Pending
      </v-btn>
    </v-col>

    <v-col cols="12">
      <v-card style="border-radius: 16px; overflow: hidden;" elevation="4">
        <!-- Toolbar -->
        <v-toolbar flat style="background: #1a237e; color: white; min-height: 64px;">
          <v-toolbar-title class="d-flex align-center">
            <v-avatar size="40" color="#311B92" class="mr-3">
              <v-icon small color="white">fas fa-chart-line</v-icon>
            </v-avatar>
            <div class="d-flex flex-column">
              <span style="font-weight: 800; font-size: 1.1rem;">Investments</span>
              <span style="font-size: 0.75rem; opacity: 0.8;">
                {{ filteredInvestments.length }} shown • {{ investments.length }} total
              </span>
            </div>
          </v-toolbar-title>
          <v-spacer></v-spacer>

          <v-btn
            icon
            small
            class="white--text mr-1"
            :style="viewMode === 'table' ? 'background: rgba(255,255,255,0.2);' : ''"
            @click="viewMode = 'table'"
          >
            <v-icon small>fas fa-table</v-icon>
          </v-btn>
          <v-btn
            icon
            small
            class="white--text mr-3"
            :style="viewMode === 'grouped' ? 'background: rgba(255,255,255,0.2);' : ''"
            @click="viewMode = 'grouped'"
          >
            <v-icon small>fas fa-users</v-icon>
          </v-btn>

          <v-text-field
            v-model="search"
            append-icon="mdi-magnify"
            label="Search..."
            dense
            hide-details
            filled
            rounded
            dark
            style="max-width: 260px;"
          ></v-text-field>
        </v-toolbar>

        <!-- Filters -->
        <v-card-text class="pt-4 pb-2">
          <v-row>
            <v-col cols="12" md="7" class="py-1">
              <div style="font-size: 0.7rem; font-weight: 700; color: #888; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 6px;">
                Status
              </div>
              <v-chip-group v-model="statusFilter" mandatory active-class="chip-active">
                <v-chip small value="all" class="filter-chip"><v-icon x-small left>fas fa-layer-group</v-icon>All</v-chip>
                <v-chip small value="active" class="filter-chip"><v-icon x-small left>fas fa-check-circle</v-icon>Active</v-chip>
                <v-chip small value="pending_first_payment" class="filter-chip"><v-icon x-small left>fas fa-hourglass-half</v-icon>Pending</v-chip>
                <v-chip small value="completed" class="filter-chip"><v-icon x-small left>fas fa-flag-checkered</v-icon>Done</v-chip>
                <v-chip small value="cancelled" class="filter-chip"><v-icon x-small left>fas fa-ban</v-icon>Cancelled</v-chip>
              </v-chip-group>
            </v-col>

            <v-col cols="12" md="5" class="py-1">
              <div style="font-size: 0.7rem; font-weight: 700; color: #888; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 6px;">
                Type
              </div>
              <v-chip-group v-model="typeFilter" mandatory active-class="chip-active">
                <v-chip small value="all" class="filter-chip"><v-icon x-small left>fas fa-layer-group</v-icon>All</v-chip>
                <v-chip small value="monthly" class="filter-chip"><v-icon x-small left>fas fa-calendar-alt</v-icon>Monthly</v-chip>
                <v-chip small value="one_time" class="filter-chip"><v-icon x-small left>fas fa-bolt</v-icon>One-Time</v-chip>
              </v-chip-group>
            </v-col>
          </v-row>
        </v-card-text>

        <!-- Stats -->
        <v-row class="mx-3 mb-2 mt-1">
          <v-col cols="6" sm="3" v-for="(stat, i) in stats" :key="i">
            <v-card flat style="border-radius: 12px; border-left: 4px solid;" :style="`background: ${stat.bg}; border-color: ${stat.color};`">
              <v-card-text class="text-center pa-3">
                <v-icon size="26" :color="stat.color">{{ stat.icon }}</v-icon>
                <div style="font-size: 1.2rem; font-weight: 800; color: #333; margin-top: 4px;">{{ stat.value }}</div>
                <div style="font-size: 0.65rem; font-weight: 600; color: #888; text-transform: uppercase; letter-spacing: 0.5px;">{{ stat.label }}</div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Content -->
        <v-card-text class="pt-2">
          <v-skeleton-loader v-if="loading" type="table-tbody"></v-skeleton-loader>

          <!-- TABLE VIEW -->
          <v-data-table
            v-else-if="viewMode === 'table'"
            :headers="headers"
            :items="filteredInvestments"
            item-key="id"
            :items-per-page="10"
            :search="search"
            @click:row="openInvestment"
            mobile-breakpoint="768"
            style="border-radius: 12px; overflow: hidden;"
          >
            <template #[`item.user`]="{ item }">
              <div class="d-flex align-center py-1">
                <v-avatar size="34" color="#00695C" class="mr-2">
                  <span class="white--text" style="font-size: 0.85rem; font-weight: 700;">{{ getInitial(item) }}</span>
                </v-avatar>
                <div>
                  <div style="font-weight: 600; font-size: 0.9rem;">{{ getName(item) }}</div>
                  <div style="font-size: 0.75rem; color: #888;">{{ getContact(item) }}</div>
                </div>
              </div>
            </template>

            <template #[`item.investment_amount`]="{ item }">
              <div>
                <span style="font-weight: 700; color: #00695C;">{{ formatAmount(item.investment_amount) }} FCFA</span>
                <div style="font-size: 0.75rem; color: #888;">Paid: {{ formatAmount(item.total_paid) }}</div>
              </div>
            </template>

            <template #[`item.payment_type`]="{ item }">
              <v-chip x-small dark :color="item.payment_type === 'monthly' ? '#283593' : '#6A1B9A'" style="font-weight: 500;">
                {{ item.payment_type === 'monthly' ? 'Monthly' : 'One-Time' }}
              </v-chip>
            </template>

            <template #[`item.status`]="{ item }">
              <v-chip x-small dark :color="getStatusColor(item.status)" style="font-weight: 500;">
                <v-icon x-small left>{{ getStatusIcon(item.status) }}</v-icon>
                {{ formatStatus(item.status) }}
              </v-chip>
            </template>

            <template #[`item.progress`]="{ item }">
              <div style="min-width: 100px;">
                <div class="d-flex justify-space-between mb-1">
                  <span style="font-size: 0.75rem; font-weight: 700;">{{ item.progress || 0 }}%</span>
                  <span style="font-size: 0.75rem; color: #888;" v-if="item.next_due_date">
                    <v-icon x-small>fas fa-calendar</v-icon> {{ item.next_due_date }}
                  </span>
                </div>
                <v-progress-linear :value="item.progress || 0" height="5" rounded :color="getProgressColor(item.progress || 0)"></v-progress-linear>
              </div>
            </template>

            <template #[`item.interest_earned`]="{ item }">
              <span style="font-weight: 700; color: #2E7D32;">+{{ formatAmount(item.interest_earned) }}</span>
            </template>

            <template #[`item.creation_date`]="{ item }">
              <span style="font-size: 0.8rem; color: #666;">
                <v-icon x-small color="#888">fas fa-clock</v-icon> {{ item.creation_date || '-' }}
              </span>
            </template>

            <!-- COLONNE ACTIONS (couleur inline sur l'icône + animation pulse) -->
            <template #[`item.actions`]="{ item }">
              <div @click.stop>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      small
                      :disabled="!isEligibleForDelete(item)"
                      v-bind="attrs"
                      v-on="on"
                      @click="isEligibleForDelete(item) ? askConfirmHardDelete(item) : null"
                    >
                      <v-icon
                        small
                        :style="{
                          color: isEligibleForDelete(item) ? 'red' : '#bdbdbd',
                          animation: isEligibleForDelete(item) ? 'pulse 1.5s infinite' : 'none'
                        }"
                      >
                        fas fa-trash-alt
                      </v-icon>
                    </v-btn>
                  </template>
                  <span>{{ isEligibleForDelete(item) ? 'Delete permanently' : 'Cannot delete active investment' }}</span>
                </v-tooltip>
              </div>
            </template>
          </v-data-table>

          <!-- GROUPED VIEW : 1 ligne = 1 user -->
          <div v-else>
            <div
              v-for="(group, idx) in groupedInvestments"
              :key="group.userId"
              class="d-flex align-center pa-3 mb-3"
              style="border-radius: 14px; border: 1px solid #e0e0e0; cursor: pointer; transition: all 0.2s ease; background: white;"
              @click="openUserDialog(group)"
              @mouseenter="$event.currentTarget.style.background='#fafafa'"
              @mouseleave="$event.currentTarget.style.background='white'"
            >
              <v-avatar size="44" :color="getUserColor(idx)" class="mr-3">
                <span class="white--text" style="font-weight: 700; font-size: 1.1rem;">{{ group.initial }}</span>
              </v-avatar>

              <div class="flex-grow-1">
                <div class="d-flex align-center flex-wrap">
                  <span style="font-weight: 700; font-size: 1rem; color: #333;" class="mr-2">{{ group.name }}</span>
                  <v-chip x-small color="#00695C" dark style="font-weight: 500;">{{ group.items.length }} investments</v-chip>
                  <v-chip x-small :color="group.activeCount > 0 ? '#2E7D32' : '#757575'" dark class="ml-1" style="font-weight: 500;">
                    {{ group.activeCount }} active
                  </v-chip>
                </div>
                <div style="font-size: 0.75rem; color: #888; margin-top: 2px;">
                  {{ group.phone || 'No phone' }} • {{ group.email || 'No email' }}
                </div>
              </div>

              <div class="text-right mr-3 hidden-xs-only">
                <div style="font-weight: 700; color: #00695C; font-size: 0.95rem;">{{ formatAmount(group.totalInvested) }} FCFA</div>
                <div style="font-size: 0.7rem; color: #888;">Total invested</div>
              </div>

              <div class="text-right hidden-xs-only mr-3">
                <div style="font-weight: 700; color: #2E7D32; font-size: 0.95rem;">+{{ formatAmount(group.totalInterest) }}</div>
                <div style="font-size: 0.7rem; color: #888;">Total interest</div>
              </div>

              <v-icon small color="#888">fas fa-chevron-right</v-icon>
            </div>
          </div>

          <!-- Empty -->
          <v-alert
            v-if="!loading && filteredInvestments.length === 0"
            type="info"
            text
            style="border-radius: 12px; margin-top: 16px;"
          >
            <div class="d-flex align-center">
              <v-icon large left color="#90CAF9">fas fa-search</v-icon>
              <div>
                <div style="font-weight: 700;">No investments found</div>
                <div style="font-size: 0.8rem; color: #888;">Try adjusting filters or search terms.</div>
              </div>
            </div>
          </v-alert>
        </v-card-text>
      </v-card>
    </v-col>

    <!-- DIALOG : investissements d'un utilisateur -->
    <v-dialog v-model="dialogOpen" max-width="720" scrollable>
      <v-card v-if="selectedGroup" style="border-radius: 16px; overflow: hidden;">
        <div class="pa-4 d-flex align-center" style="background: #1a237e; color: white;">
          <v-avatar size="48" :color="selectedGroup.color || '#311B92'" class="mr-3">
            <span class="white--text" style="font-weight: 700; font-size: 1.2rem;">{{ selectedGroup.initial }}</span>
          </v-avatar>
          <div class="flex-grow-1">
            <div style="font-weight: 700; font-size: 1.15rem;">{{ selectedGroup.name }}</div>
            <div style="font-size: 0.8rem; opacity: 0.85;">
              {{ selectedGroup.items.length }} investments • {{ formatAmount(selectedGroup.totalInvested) }} FCFA invested
            </div>
          </div>
          <v-btn icon dark @click="dialogOpen = false">
            <v-icon>fas fa-times</v-icon>
          </v-btn>
        </div>

        <v-card-text class="pa-4" style="background: #f5f5f5;">
          <div
            v-for="item in selectedGroup.items"
            :key="item.id"
            class="pa-3 mb-3"
            style="border-radius: 12px; background: white; border: 1px solid #e0e0e0; cursor: pointer; transition: all 0.15s ease;"
            @click="openFromDialog(item)"
            @mouseenter="$event.currentTarget.style.boxShadow='0 4px 12px rgba(0,0,0,0.08)'; $event.currentTarget.style.borderColor='#1a237e'"
            @mouseleave="$event.currentTarget.style.boxShadow='none'; $event.currentTarget.style.borderColor='#e0e0e0'"
          >
            <div class="d-flex justify-space-between align-start mb-2">
              <div>
                <div style="font-weight: 700; color: #00695C; font-size: 1rem;">
                  {{ formatAmount(item.investment_amount) }} FCFA
                </div>
                <div style="font-size: 0.75rem; color: #888;">Paid: {{ formatAmount(item.total_paid) }}</div>
              </div>
              <div class="d-flex flex-column align-end">
                <v-chip x-small dark :color="getStatusColor(item.status)" class="mb-1" style="font-weight: 500;">
                  <v-icon x-small left>{{ getStatusIcon(item.status) }}</v-icon>
                  {{ formatStatus(item.status) }}
                </v-chip>
                <v-chip x-small dark :color="item.payment_type === 'monthly' ? '#283593' : '#6A1B9A'" style="font-weight: 500;">
                  {{ item.payment_type === 'monthly' ? 'Monthly' : 'One-Time' }}
                </v-chip>
              </div>
            </div>

            <div class="d-flex justify-space-between align-center">
              <div style="min-width: 120px; flex-grow: 1; margin-right: 16px;">
                <div class="d-flex justify-space-between mb-1">
                  <span style="font-size: 0.75rem; font-weight: 700; color: #555;">Progress {{ item.progress || 0 }}%</span>
                  <span style="font-size: 0.75rem; color: #888;" v-if="item.next_due_date">
                    <v-icon x-small>fas fa-calendar</v-icon> {{ item.next_due_date }}
                  </span>
                </div>
                <v-progress-linear :value="item.progress || 0" height="6" rounded :color="getProgressColor(item.progress || 0)"></v-progress-linear>
              </div>

              <div class="text-right hidden-xs-only">
                <div style="font-weight: 700; color: #2E7D32;">+{{ formatAmount(item.interest_earned) }}</div>
                <div style="font-size: 0.7rem; color: #888;">Interest</div>
              </div>
            </div>

            <!-- ACTIONS dans le dialogue (couleur inline + animation) -->
            <div class="d-flex justify-space-between align-center mt-2 pt-2" style="border-top: 1px solid #f0f0f0;">
              <span style="font-size: 0.75rem; color: #888;">
                <v-icon x-small color="#888">fas fa-clock</v-icon> {{ item.creation_date || 'No date' }}
              </span>
              <div @click.stop>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      x-small
                      :disabled="!isEligibleForDelete(item)"
                      v-bind="attrs"
                      v-on="on"
                      @click="isEligibleForDelete(item) ? askConfirmHardDelete(item) : null"
                    >
                      <v-icon
                        x-small
                        :style="{
                          color: isEligibleForDelete(item) ? 'red' : '#bdbdbd',
                          animation: isEligibleForDelete(item) ? 'pulse 1.5s infinite' : 'none'
                        }"
                      >
                        fas fa-trash-alt
                      </v-icon>
                    </v-btn>
                  </template>
                  <span>{{ isEligibleForDelete(item) ? 'Delete permanently' : 'Cannot delete active investment' }}</span>
                </v-tooltip>
              </div>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- DIALOGUE DE CONFIRMATION HARD DELETE (adaptatif) -->
    <v-dialog
      v-model="confirmDialog.show"
      max-width="560"
      persistent
      transition="dialog-bottom-transition"
    >
      <v-card style="border-radius: 20px; overflow: hidden;" elevation="24">
        <div
          class="d-flex flex-column align-center pa-6"
          :style="`background: linear-gradient(135deg, ${confirmDialog.gradientFrom || '#C62828'} 0%, ${confirmDialog.gradientTo || '#E53935'} 100%); color: white;`"
        >
          <v-avatar size="64" :color="confirmDialog.iconBg || '#C62828'" style="border: 3px solid white;">
            <v-icon size="36" color="white">{{ confirmDialog.icon || 'fas fa-trash-alt' }}</v-icon>
          </v-avatar>
          <div class="mt-4 text-h6 font-weight-bold">{{ confirmDialog.title }}</div>
        </div>

        <v-card-text class="pa-6 text-center">
          <p class="text-body-1 mb-2" style="color: #444; line-height: 1.6;" v-html="confirmDialog.message"></p>

          <div v-if="confirmDialog.warnings && confirmDialog.warnings.length" class="mt-4">
            <v-chip
              v-for="(warn, i) in confirmDialog.warnings"
              :key="i"
              small
              :color="warn.color || 'grey'"
              text-color="white"
              class="mr-1 mb-1 font-weight-medium"
            >
              <v-icon x-small left>{{ warn.icon || 'fas fa-info-circle' }}</v-icon>
              {{ warn.text }}
            </v-chip>
          </div>
        </v-card-text>

        <v-card-actions class="px-6 pb-6 justify-center">
          <v-btn large text color="#757575" @click="confirmDialog.show = false" class="mr-3">Cancel</v-btn>
          <v-btn
            large
            :color="confirmDialog.buttonColor || '#C62828'"
            class="white--text px-6"
            @click="executeHardDelete"
            :loading="confirmDialog.loading"
          >
            <v-icon left>{{ confirmDialog.buttonIcon || 'fas fa-trash-alt' }}</v-icon>
            {{ confirmDialog.buttonText }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
import axios from 'axios'

const USER_COLORS = ['#1A237E', '#00695C', '#283593', '#6A1B9A', '#C62828', '#E65100', '#2E7D32', '#0277BD']

export default {
  data() {
    return {
      viewMode: 'table',
      dialogOpen: false,
      selectedGroup: null,
      headers: [
        { text: 'Client', value: 'user', sortable: false },
        { text: 'Amount', value: 'investment_amount' },
        { text: 'Type', value: 'payment_type', sortable: false },
        { text: 'Status', value: 'status', sortable: false },
        { text: 'Progress', value: 'progress', sortable: false },
        { text: 'Interest', value: 'interest_earned' },
        { text: 'Date', value: 'creation_date' },
        { text: 'Actions', value: 'actions', sortable: false, align: 'center' },
      ],
      investments: [],
      loading: false,
      search: '',
      statusFilter: 'all',
      typeFilter: 'all',

      confirmDialog: {
        show: false,
        item: null,
        title: '',
        message: '',
        buttonText: '',
        buttonColor: '#C62828',
        buttonIcon: 'fas fa-trash-alt',
        icon: 'fas fa-trash-alt',
        iconBg: '#C62828',
        gradientFrom: '#C62828',
        gradientTo: '#E53935',
        warnings: [],
        loading: false,
      }
    }
  },

  computed: {
    filteredInvestments() {
      const list = Array.isArray(this.investments) ? this.investments : []
      return list.filter(inv => {
        const okStatus = this.statusFilter === 'all' || inv.status === this.statusFilter
        const okType = this.typeFilter === 'all' || inv.payment_type === this.typeFilter
        return okStatus && okType
      })
    },

    groupedInvestments() {
      const groups = {}
      this.filteredInvestments.forEach((inv) => {
        const userId = inv.user?.id || 'unknown'
        if (!groups[userId]) {
          groups[userId] = {
            userId,
            name: this.getName(inv),
            initial: this.getInitial(inv),
            phone: inv.user?.phone_number || '',
            email: inv.user?.email || '',
            items: [],
            totalInvested: 0,
            totalInterest: 0,
            activeCount: 0,
            color: USER_COLORS[Object.keys(groups).length % USER_COLORS.length],
          }
        }
        groups[userId].items.push(inv)
        groups[userId].totalInvested += parseFloat(inv.investment_amount || 0)
        groups[userId].totalInterest += parseFloat(inv.interest_earned || 0)
        if (inv.status === 'active') groups[userId].activeCount++
      })
      return Object.values(groups).sort((a, b) => b.totalInvested - a.totalInvested)
    },

    stats() {
      return [
        { icon: 'fas fa-list', value: this.filteredInvestments.length, label: 'Total', color: '#00695C', bg: '#E0F2F1' },
        { icon: 'fas fa-coins', value: this.formatAmount(this.totalInvested), label: 'Invested', color: '#2E7D32', bg: '#E8F5E9' },
        { icon: 'fas fa-check-circle', value: this.totalActive, label: 'Active', color: '#1565C0', bg: '#E3F2FD' },
        { icon: 'fas fa-percentage', value: this.formatAmount(this.totalInterest), label: 'Interest', color: '#6A1B9A', bg: '#F3E5F5' },
      ]
    },

    totalInvested() {
      return this.investments.reduce((s, i) => s + parseFloat(i.investment_amount || 0), 0)
    },
    totalActive() {
      return this.investments.reduce((s, i) => s + (i.status === 'active' ? 1 : 0), 0)
    },
    totalInterest() {
      return this.investments.reduce((s, i) => s + parseFloat(i.interest_earned || 0), 0)
    },
  },

  methods: {
    getName(item) { return item.user?.username || 'Unknown' },
    getInitial(item) { return (item.user?.username || '?')[0].toUpperCase() },
    getContact(item) { return item.user?.phone_number || item.user?.email || '-' },
    getUserColor(idx) { return USER_COLORS[idx % USER_COLORS.length] },

    openUserDialog(group) {
      this.selectedGroup = group
      this.dialogOpen = true
    },

    openFromDialog(item) {
      this.dialogOpen = false
      this.$nextTick(() => {
        this.openInvestment(item)
      })
    },

    formatAmount(v) {
      if (v == null || v === '') return '0'
      const n = Number(v)
      return Number.isNaN(n) ? String(v) : new Intl.NumberFormat('fr-FR').format(n)
    },

    formatStatus(s) {
      return { active: 'Active', completed: 'Done', cancelled: 'Cancelled', pending_first_payment: 'Pending' }[s] || s
    },

    getStatusIcon(s) {
      return { active: 'fas fa-check-circle', completed: 'fas fa-flag-checkered', cancelled: 'fas fa-ban', pending_first_payment: 'fas fa-hourglass-half' }[s] || 'fas fa-question'
    },

    getStatusColor(s) {
      return { active: '#2E7D32', completed: '#1565C0', cancelled: '#C62828', pending_first_payment: '#E65100' }[s] || '#757575'
    },

    getProgressColor(p) {
      if (p >= 100) return '#2E7D32'
      if (p >= 50) return '#1565C0'
      if (p >= 25) return '#F57C00'
      return '#C62828'
    },

    isEligibleForDelete(item) {
      return item.status !== 'active'
    },

    askConfirmHardDelete(item) {
      if (!this.isEligibleForDelete(item)) return

      const name = this.getName(item)
      const amount = this.formatAmount(item.investment_amount)
      const paid = this.formatAmount(item.total_paid)
      const status = item.status
      const paymentCount = item.payment_count || 0

      let title = 'Delete Investment'
      let message = `Are you sure you want to <strong>permanently delete</strong> the investment of <strong>${name}</strong> for <strong>${amount} FCFA</strong>?`
      let warnings = []
      let gradientFrom = '#C62828'
      let gradientTo = '#E53935'
      let buttonText = 'Delete permanently'
      let buttonColor = '#C62828'

      if (status === 'pending_first_payment' && (item.total_paid === 0 || item.total_paid == null)) {
        title = 'Delete Pending Investment'
        message += '<br>This investment has not yet received any payment.'
        warnings = [
          { text: 'No payments recorded', color: 'green darken-1', icon: 'fas fa-check-circle' }
        ]
      } else if (status === 'cancelled' && (item.total_paid === 0 || item.total_paid == null)) {
        title = 'Delete Cancelled Investment'
        message += '<br>The investment was previously cancelled.'
        warnings = [
          { text: 'Previously cancelled', color: 'grey', icon: 'fas fa-ban' }
        ]
      } else {
        if (status === 'completed') {
          title = 'Delete Completed Investment'
          message += '<br><strong>Warning:</strong> This investment is marked as <strong>completed</strong>.'
          gradientFrom = '#BF360C'
          gradientTo = '#E64A19'
          buttonColor = '#BF360C'
        } else if (status === 'cancelled') {
          title = 'Delete Cancelled Investment'
          message += '<br><strong>Warning:</strong> This investment was cancelled but has recorded payments.'
          gradientFrom = '#BF360C'
          gradientTo = '#E64A19'
          buttonColor = '#BF360C'
        } else if (status === 'pending_first_payment' && item.total_paid > 0) {
          title = 'Delete Pending Investment (with payments)'
          message += '<br><strong>Warning:</strong> This pending investment already has recorded payments.'
          gradientFrom = '#BF360C'
          gradientTo = '#E64A19'
          buttonColor = '#BF360C'
        }

        if (item.total_paid > 0) {
          warnings.push({ text: `${paid} FCFA already paid`, color: 'orange darken-2', icon: 'fas fa-money-bill-wave' })
        }
        if (paymentCount > 0) {
          warnings.push({ text: `${paymentCount} payment(s) recorded`, color: 'orange darken-2', icon: 'fas fa-list-ul' })
        }
        warnings.push({ text: 'Irreversible action', color: 'deep-orange darken-4', icon: 'fas fa-exclamation-triangle' })
        warnings.push({ text: 'All related transactions will be lost', color: 'grey darken-2', icon: 'fas fa-database' })
      }

      this.confirmDialog = {
        show: true,
        item: item,
        title,
        message,
        buttonText,
        buttonColor,
        buttonIcon: 'fas fa-trash-alt',
        icon: 'fas fa-trash-alt',
        iconBg: buttonColor,
        gradientFrom,
        gradientTo,
        warnings,
        loading: false,
      }
    },

    async executeHardDelete() {
      const item = this.confirmDialog.item
      if (!item) return

      this.confirmDialog.loading = true
      try {
        const { data } = await axios.post(`/api/v1/manager/investments/${item.id}/hard-delete/`)
        if (data.success) {
          this.showSnack('Investment permanently deleted', '#C62828')
          this.getInvestments()
        } else {
          throw new Error(data.error || 'Failed to delete')
        }
      } catch (e) {
        this.showSnack(e.response?.data?.error || 'Permanent deletion failed', '#B71C1C')
      } finally {
        this.confirmDialog.loading = false
        this.confirmDialog.show = false
      }
    },

    showSnack(message, color) {
      this.$store.commit('setSnackBarMessage', message)
      this.$store.commit('setSnackBarColor', color)
      this.$store.commit('activateSnackBar', true)
    },

    async getInvestments() {
      this.loading = true
      try {
        const { data = {} } = await axios.get('/api/v1/manager/investments/list/')
        this.investments = data.investments || []
      } catch (e) {
        this.showSnack(e.response?.data?.error || 'Failed to load investments', '#B71C1C')
      } finally {
        this.loading = false
      }
    },

    openInvestment(item) {
      this.$router.push({ name: 'manager.investments.detail', params: { investment_id: item.id } })
    },
  },

  mounted() {
    document.title = 'Manager | Investments'
    this.getInvestments()
  },
}
</script>

<style scoped>
.filter-chip {
  border-radius: 999px;
  font-weight: 500;
}

.chip-active {
  background: #EDE7F6 !important;
  color: #4527A0 !important;
  font-weight: 700;
}

/* Animation de pulse pour les icônes rouges */
@keyframes pulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
  }
}

::v-deep .v-data-table tbody tr {
  cursor: pointer;
}

::v-deep .v-data-table tbody tr:hover {
  background: #F5F5F5 !important;
}

::v-deep .v-data-table th {
  font-weight: 700 !important;
  font-size: 0.7rem !important;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #555 !important;
  background: #FAFAFA !important;
}
</style>
